import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Download, RefreshCw, Clock, FileText, Printer, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const InspirationalSection = () => {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/inspirational/latest`);
      const data = await res.json();
      if (data) setLatest(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/inspirational/history`);
      const data = await res.json();
      setHistory(data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLatest(); fetchHistory(); }, [fetchLatest, fetchHistory]);

  const generateMessage = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/inspirational/generate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setLatest(data.message);
        setSelectedMsg(null);
        fetchHistory();
      }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const downloadPoster = (msgId, size) => {
    // Use relative URL so the download follows the user's current domain
    // (production: cccvob.com, staging: preview URL). Avoids hardcoded preview links.
    window.open(`/api/inspirational/poster/${msgId}?size=${size}`, '_blank');
  };

  const active = selectedMsg || latest;

  const bgThemes = {
    'bg_sunlight.png': 'from-amber-400 via-orange-300 to-yellow-200',
    'bg_celestial.png': 'from-indigo-800 via-blue-900 to-slate-900',
    'bg_nature.png': 'from-teal-600 via-cyan-700 to-slate-800'
  };

  const bgPreviewColors = {
    'bg_sunlight.png': 'from-amber-600/20 to-orange-500/10',
    'bg_celestial.png': 'from-indigo-600/20 to-blue-500/10',
    'bg_nature.png': 'from-teal-600/20 to-cyan-500/10'
  };

  return (
    <div className="space-y-6" data-testid="inspirational-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 rounded-2xl p-5 text-white shadow-xl"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-md">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold" data-testid="inspirational-title">Weekly Inspirational Message</h3>
              <p className="text-amber-100 text-sm">AI-generated spiritual posters for the church</p>
            </div>
          </div>
          <motion.button
            onClick={generateMessage}
            disabled={generating}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="generate-message-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-amber-700 rounded-xl font-bold shadow-lg hover:bg-amber-50 disabled:opacity-50 transition-all"
          >
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating...' : 'Generate New Message'}
          </motion.button>
        </div>
      </motion.div>

      {/* Preview Card */}
      <AnimatePresence mode="wait">
        {active ? (
          <motion.div
            key={active.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            data-testid="message-preview-card"
          >
            <div className={`rounded-2xl overflow-hidden shadow-2xl border border-slate-200`}>
              {/* Poster Preview */}
              <div className={`bg-gradient-to-br ${bgPreviewColors[active.background] || bgPreviewColors['bg_sunlight.png']} p-8`}>
                <div className="max-w-2xl mx-auto text-center space-y-4">
                  {/* Church label */}
                  <p className="text-xs font-semibold tracking-[0.2em] text-amber-700/70 uppercase">
                    Celestial Church of Christ &middot; Valley of Berachah Parish
                  </p>

                  {/* Badge */}
                  <div className="inline-block px-4 py-1 rounded-full bg-amber-500/15 border border-amber-400/30">
                    <span className="text-xs font-medium text-amber-700 tracking-wider uppercase">
                      Weekly Inspirational Message
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-800 leading-tight" data-testid="message-title">
                    {active.title}
                  </h2>

                  {/* Divider */}
                  <div className="flex justify-center"><div className="w-16 h-0.5 bg-amber-500/50 rounded-full" /></div>

                  {/* Message */}
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-medium" data-testid="message-body">
                    {active.message}
                  </p>

                  {/* Bible verse */}
                  {active.bible_verse && (
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-amber-200/50">
                      <p className="text-sm italic text-slate-600 leading-relaxed" data-testid="message-verse">
                        "{active.bible_verse}"
                      </p>
                      <p className="text-sm font-bold text-amber-700 mt-2" data-testid="message-reference">
                        — {active.bible_reference}
                      </p>
                    </div>
                  )}

                  {/* Theme */}
                  <p className="text-xs text-slate-400">
                    Theme: {active.theme} &middot; {active.week_of}
                  </p>
                </div>
              </div>

              {/* Download Actions */}
              <div className="bg-white p-4 flex flex-wrap items-center justify-center gap-3">
                <motion.button
                  onClick={() => downloadPoster(active.id, 'a4')}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="download-a4-btn"
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  <Download className="w-4 h-4" />
                  Download A4 Poster
                </motion.button>
                <motion.button
                  onClick={() => downloadPoster(active.id, 'a3')}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="download-a3-btn"
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  <Printer className="w-4 h-4" />
                  Download A3 Poster
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300"
          >
            <Sparkles className="w-12 h-12 text-amber-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-500">No Messages Yet</h3>
            <p className="text-slate-400 text-sm mt-1">Click "Generate New Message" to create your first inspirational poster</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Toggle */}
      {history.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history-btn"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 font-medium text-sm transition-colors"
          >
            <Clock className="w-4 h-4" />
            Past Messages ({history.length})
            <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid gap-3 mt-3">
                  {history.map((msg) => (
                    <motion.div
                      key={msg.id}
                      onClick={() => { setSelectedMsg(msg.id === selectedMsg?.id ? null : msg); }}
                      whileHover={{ scale: 1.01 }}
                      data-testid={`history-item-${msg.id}`}
                      className={`cursor-pointer p-4 rounded-xl border transition-all ${
                        selectedMsg?.id === msg.id
                          ? 'border-amber-400 bg-amber-50 shadow-md'
                          : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-slate-800 truncate">{msg.title}</h4>
                          <p className="text-sm text-slate-500 truncate">{msg.message}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-3 shrink-0">
                          <span className="text-xs text-slate-400 whitespace-nowrap">{msg.week_of}</span>
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadPoster(msg.id, 'a4'); }}
                              className="p-1.5 rounded-lg bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors"
                              title="Download A4"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadPoster(msg.id, 'a3'); }}
                              className="p-1.5 rounded-lg bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors"
                              title="Download A3"
                            >
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default InspirationalSection;
